import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";
import { NeuButton, NeuInput } from "../../components/neu/Neu";
import { SignaturePad } from "../purchase/SignaturePad";
import { getTransfer, destinationConfirm } from "../../api/warehouse";
import { localName } from "../../lib/localName";

// شاشة استلام بنقص: تعديل الكميات + توقيع إلكتروني بالإصبع (حبر أزرق).
export const TransferReceiveForm = ({ transferId, onClose, onDone }) => {
  const { t, i18n } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getTransfer(transferId).then((d) => { setDoc(d);
      setRows((d.lines || []).map((l) => ({ ...l, received_qty: Number(l.qty) || 0 }))); })
      .catch(() => setDoc(false));
  }, [transferId]);

  const num = (n) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(n || 0);

  const save = async () => {
    setBusy(true);
    try {
      const adjustments = rows.map((r) => ({ item_id: r.item_id, received_qty: Number(r.received_qty) || 0 }));
      await destinationConfirm(transferId, "shortage", note, adjustments, signature);
      toast.success(t("warehouse.tr.shortagePosted")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || t("errors.generic")); }
    finally { setBusy(false); }
  };

  if (!doc) return null;
  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-background" data-testid="transfer-receive">
      <div className="flex flex-shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-amber-500 to-orange-500 px-6 pb-4 pt-[max(1rem,calc(env(safe-area-inset-top)+0.75rem))] text-white shadow-xl">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-6 w-6" />
          <div><p className="text-lg font-black sm:text-xl">{t("warehouse.tr.shortageTitle")}</p>
            <p className="text-[11px] opacity-80 num font-mono">{doc.number} · {localName(doc.from_wh)} → {localName(doc.to_wh)} — {t("warehouse.tr.keeper")}: {doc.to_wh?.keeper_name || "—"}</p></div>
        </div>
        <button onClick={onClose} data-testid="trf-recv-close"
                className="rounded-full bg-white/15 p-2 hover:bg-white/25"><X className="h-6 w-6" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
        <div className="mx-auto grid max-w-5xl gap-4">
          <div className="rounded-2xl bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-200">
            {t("warehouse.tr.shortageHint")}</div>
          <div className="overflow-hidden rounded-2xl shadow-neu-in">
            <table className="w-full text-xs">
              <thead className="bg-amber-500/10 text-[10px]">
                <tr>{["item", "released", "received", "diff"].map((k) =>
                  <th key={k} className="p-2 text-start font-bold">{t(`warehouse.tr.recvCols.${k}`)}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const released = Number(r.qty) || 0;
                  const diff = (Number(r.received_qty) || 0) - released;
                  return (<tr key={r.item_id} className="border-b border-border/30">
                    <td className="p-2"><p className="font-bold">{r.item_name}</p>
                      <p className="num font-mono text-[10px] text-muted-foreground">{r.item_code} · {r.uom}</p></td>
                    <td className="p-2 num font-mono">{num(released)}</td>
                    <td className="p-2 w-28"><NeuInput dir="ltr" type="number"
                        data-testid={`trf-recv-${r.item_id}`} value={r.received_qty} max={released} min={0}
                        onChange={(e) => setRows(rows.map((x, ix) => ix === i
                          ? { ...x, received_qty: Math.min(Math.max(Number(e.target.value) || 0, 0), released) } : x))}
                        className="h-8 text-center" /></td>
                    <td className={`p-2 num font-mono font-bold ${diff < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {diff > 0 ? "+" : ""}{num(diff)}</td></tr>);})}
              </tbody>
            </table>
          </div>
          <p className="label-xs">{t("warehouse.tr.signHere")} ({t("sign.blueInk")}):</p>
          <div className="rounded-2xl bg-white p-2 shadow-neu-in">
            <SignaturePad onChange={setSignature} /></div>
          <NeuInput data-testid="trf-recv-note" value={note}
                    placeholder={t("warehouse.tr.notePlaceholder")}
                    onChange={(e) => setNote(e.target.value)} />
          <div className="flex justify-end border-t border-border/40 pt-4">
            <NeuButton variant="primary" data-testid="trf-recv-save" disabled={busy || !signature} onClick={save}
                       className="bg-amber-500 text-white hover:bg-amber-600">
              {t("warehouse.tr.postShortage")}</NeuButton>
          </div>
        </div>
      </div>
    </div>);
};
